import React, { useMemo, useRef, useState } from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import {
  View,
  StyleSheet,
  Pressable,
  Image,
  Platform,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { BottomSheetModal, BottomSheetTextInput } from '@gorhom/bottom-sheet';
import { useDispatch } from 'react-redux';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import Receipt from '../checkout/components/Receipt';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import CustomBottomSheet from '../../components/Reusables/bottomSheet/CustomBottomSheet';
import Button from '../../components/Reusables/Button';
import Feather from '@react-native-vector-icons/feather/static';
import PhoneInput, {
  ICountry,
  IPhoneInputRef,
} from 'rn-international-phone-number';
import Typography from '../../components/Reusables/Typography';
import DemoPaymentModal from './components/DemoPaymentModal';
import { AppSnackbar } from '../../services/AppSnackbar';
import { GTFonts, theme } from '../../theme';
import { useAppSelector } from '../../redux/helper';
import {
  resetCheckoutPaymentIntent,
  setCheckoutPaymentIntent,
  setCheckoutPaymentSelection,
} from '../../redux/checkoutSlice';
import { clearDemoData } from '../../models/demo';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import {
  formatNumericStringWithCommas,
  iconSize,
  sanitizeMoneyInput,
  normalizeSafaricomPhone,
  safaricomNumberValidation,
  replaceCurrencyString,
  borderWidth,
  s,
  v,
  fs,
  formatMoney,
} from '../../utils/libs';
import { useDetectedCountry } from '../../hooks/useDetectedCountry';
import {
  buildE164FromInput,
  derivePhoneInputFromValue,
  PREFERRED_COUNTRIES,
} from '../../utils/phone';
import DemoAttentionPulse from './components/DemoAttentionPulse';
import { shouldUseWebLayout } from '../../utils/layoutUtils';

type DemoCheckoutRouteParams = {
  order: any[];
  orderID: string;
  Payment_Information: any;
  ReceiptID: string;
};

const DemoCheckoutScreen = ({
  route,
  navigation,
}: {
  route: any;
  navigation: any;
}) => {
  const dispatch = useDispatch();
  const checkoutPaymentIntent = useAppSelector(
    state => state.checkout.paymentIntent,
  );
  const checkoutPaymentSelection = useAppSelector(
    state => state.checkout.paymentSelection,
  );
  const paymentIntentHydratedRef = useRef(false);
  const {
    order,
    orderID,
    Payment_Information: paymentInfoFromRoute,
  } = route.params as DemoCheckoutRouteParams;

  const [Payment_Information, setPayment_Information] = useState<any>(
    paymentInfoFromRoute || {},
  );
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [payForSelectedItemsOnly, setPayForSelectedItemsOnly] = useState(false);
  const [isloading, setIsLoading] = useState(false);

  const [payment, setPayment] = useState({
    cash: true,
    mpesa: false,
    payLater: false,
  });
  const [input, setInput] = useState('');
  const [mpesaActiveTab, setMpesaActiveTab] = useState<'STK' | 'LATEST'>(
    'LATEST',
  );
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [isSendingStk, setIsSendingStk] = useState(false);

  const MpesaBottomSheetModalRef = useRef<BottomSheetModal>(null);
  const phoneInputRef = useRef<IPhoneInputRef | null>(null);
  const [phoneInputValue, setPhoneInputValue] = useState('');
  const [phoneSelectedCountry, setPhoneSelectedCountry] = useState<
    ICountry | undefined
  >(undefined);
  const { countryCode, iCountry, currency } = useDetectedCountry();
  const { width, height } = useWindowDimensions();
  const constrainWidth = shouldUseWebLayout(width, height);
  const styles = useMemo(
    () => createDemoCheckoutStyles(constrainWidth),
    [constrainWidth],
  );
  const currencyTextCapital = useMemo(
    () => replaceCurrencyString('Ksh', currency),
    [currency],
  );

  React.useEffect(() => {
    if (!phoneSelectedCountry && iCountry) {
      setPhoneSelectedCountry(iCountry);
    }
  }, [phoneSelectedCountry, iCountry]);

  React.useEffect(() => {
    if (paymentIntentHydratedRef.current) return;
    if (!checkoutPaymentIntent) return;

    if (checkoutPaymentSelection) {
      setPayment({
        cash: checkoutPaymentSelection.cash,
        mpesa: checkoutPaymentSelection.mpesa,
        payLater: checkoutPaymentSelection.payLater,
      });
    } else if (checkoutPaymentIntent.method === 'mpesa') {
      setPayment({ cash: false, mpesa: true, payLater: false });
    } else if (checkoutPaymentIntent.method === 'pay_later') {
      setPayment({ cash: false, mpesa: false, payLater: true });
    } else {
      setPayment({ cash: true, mpesa: false, payLater: false });
    }

    setInput(
      sanitizeMoneyInput(checkoutPaymentIntent.cashAmount || ''),
    );
    setMpesaActiveTab(
      checkoutPaymentIntent.mpesa.mode === 'PAID_TO_TILL' ? 'LATEST' : 'STK',
    );
    setMpesaPhone(checkoutPaymentIntent.mpesa.phone || '');

    if (checkoutPaymentIntent.mpesa.phone) {
      const derived = derivePhoneInputFromValue(
        checkoutPaymentIntent.mpesa.phone,
      );
      setPhoneInputValue(derived.nationalNumber || '');
      if (derived.country) {
        setPhoneSelectedCountry(derived.country);
      }
    }

    paymentIntentHydratedRef.current = true;
  }, [checkoutPaymentIntent, checkoutPaymentSelection]);

  const totalAmount = useMemo(() => {
    if (!Array.isArray(order)) return 0;
    return order.reduce((acc: number, item: any) => {
      const priceType = item.bp_product_information?.bp_product_price_type;
      const quantity =
        item.bp_order_quantity - (item.bp_order_paid_quantity ?? 0);
      const mass = item.bp_order_mass - (item.bp_order_paid_mass ?? 0);
      return (
        acc +
        item.bp_product_information?.bp_product_selling_price *
          (priceType === 'unit' ? mass : quantity)
      );
    }, 0);
  }, [order]);

  const toBePaid = useMemo(() => {
    const orderAmount =
      Payment_Information?.bp_payment_amount &&
      Payment_Information?.bp_payment_amount > 0
        ? Payment_Information.bp_payment_amount
        : totalAmount;
    const alreadyPaid =
      Payment_Information?.bp_payment_already_paid_amount ?? 0;
    return Math.max(0, Number(orderAmount) - Number(alreadyPaid));
  }, [Payment_Information, totalAmount]);

  const handleSetPaymentValues = (
    cash: boolean,
    mpesa: boolean,
    payLater: boolean = false,
  ) => {
    setPayment({
      cash,
      mpesa,
      payLater,
    });
    dispatch(
      setCheckoutPaymentSelection({
        cash,
        mpesa,
        payLater,
      }),
    );
    dispatch(
      setCheckoutPaymentIntent({
        method: cash ? 'cash' : mpesa ? 'mpesa' : 'pay_later',
      }),
    );
  };

  const handleMpesaPhoneChange = (nationalValue: string) => {
    const startsWithZero = nationalValue.startsWith('0');
    if (startsWithZero) {
      nationalValue = nationalValue.slice(1);
    }
    setPhoneInputValue(nationalValue);
    const full = buildE164FromInput(
      nationalValue,
      phoneSelectedCountry || phoneInputRef.current?.getCountry?.(),
    );
    setMpesaPhone(full);
    dispatch(
      setCheckoutPaymentIntent({
        method: 'mpesa',
        mpesa: {
          phone: normalizeSafaricomPhone(full),
        },
      }),
    );
    dispatch(
      setCheckoutPaymentSelection({
        cash: false,
        mpesa: true,
        payLater: false,
      }),
    );
  };

  const handleMpesaCountryChange = (country: ICountry) => {
    setPhoneSelectedCountry(country);
    const full = buildE164FromInput(phoneInputValue, country);
    setMpesaPhone(full);
    dispatch(
      setCheckoutPaymentIntent({
        method: 'mpesa',
        mpesa: {
          phone: normalizeSafaricomPhone(full),
        },
      }),
    );
    dispatch(
      setCheckoutPaymentSelection({
        cash: false,
        mpesa: true,
        payLater: false,
      }),
    );
  };

  const openSuccessModal = (
    method: 'Cash' | 'Mpesa',
    amountPaid: number,
    mode: 'STK' | 'LATEST',
  ) => {
    const alreadyPaidBefore = Number(
      Payment_Information?.bp_payment_already_paid_amount ?? 0,
    );
    const orderAmount = Number(
      Payment_Information?.bp_payment_amount &&
        Payment_Information?.bp_payment_amount > 0
        ? Payment_Information.bp_payment_amount
        : totalAmount,
    );
    const nextAlreadyPaid = alreadyPaidBefore + amountPaid;
    const balanceRemaining = Math.max(0, orderAmount - nextAlreadyPaid);
    const change = Math.max(0, amountPaid - toBePaid);

    setPayment_Information((prev: any) => ({
      ...prev,
      bp_payment_amount: orderAmount,
      bp_payment_already_paid_amount: nextAlreadyPaid,
      bp_payment_amount_due: balanceRemaining,
      bp_payment_amount_refunded: change,
      bp_current_bill_amount: toBePaid,
    }));

    setPaymentInfo({
      payment: {
        bp_payment_updated_at: new Date().toISOString(),
        bp_payments: [
          {
            method,
            amount: amountPaid,
            mode,
          },
        ],
        bp_payment_method: method,
        bp_payment_amount: orderAmount,
        bp_current_bill_amount: toBePaid,
        bp_amount_paid: amountPaid,
        bp_payment_amount_due: balanceRemaining,
        bp_payment_refunds: [{ amount: change }],
      },
    });
    const ctx = buildAppAnalyticsContext();
    appAnalytics.track('demo_checkout_completed', {
      ...ctx,
      payment_method: method === 'Mpesa' ? 'mpesa' : 'cash',
    });
    setPaymentModalVisible(true);
  };

  const checkMaxAllowedAmount = (
    amountToBePaid: number,
    inputAmount: number,
  ) => {
    const thresholds = [
      { max: 10, multiplier: 100 },
      { max: 100, multiplier: 20 },
      { max: 500, multiplier: 10 },
      { max: 1000, multiplier: 5 },
      { max: 5000, multiplier: 3 },
      { max: Infinity, multiplier: 2 },
    ];
    const threshold = thresholds.find(t => amountToBePaid <= t.max);
    const maxAllowedAmount = amountToBePaid * (threshold?.multiplier || 1);
    return inputAmount > maxAllowedAmount;
  };

  const handlePayByCash = async () => {
    const parsed = Number(sanitizeMoneyInput(input || '0') || 0);
    if (!parsed || parsed < toBePaid) {
      AppSnackbar.show('Amount received must cover the bill', 'error', {
        durationMs: 2500,
      });
      return;
    }
    if (toBePaid && checkMaxAllowedAmount(toBePaid, parsed)) {
      AppSnackbar.show(
        'Amount received is unusually high. Please confirm the entry.',
        'error',
        { durationMs: 2500 },
      );
      return;
    }
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    setIsLoading(false);
    openSuccessModal('Cash', parsed, 'LATEST');
  };

  const handleManualMpesaPayment = async () => {
    const parsed = Number(sanitizeMoneyInput(input || '0') || 0);
    if (!parsed || parsed < toBePaid) {
      AppSnackbar.show('Amount received must cover the bill', 'error', {
        durationMs: 2500,
      });
      return;
    }
    if (toBePaid && checkMaxAllowedAmount(toBePaid, parsed)) {
      AppSnackbar.show(
        'Amount received is unusually high. Please confirm the entry.',
        'error',
        { durationMs: 2500 },
      );
      return;
    }
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 350));
    setIsLoading(false);
    openSuccessModal('Mpesa', parsed, 'LATEST');
  };

  const requestMpesaStkPush = async () => {
    const normalizedPhone = normalizeSafaricomPhone(mpesaPhone);
    if (!safaricomNumberValidation(normalizedPhone)) {
      AppSnackbar.show('Enter a valid Safaricom number', 'error', {
        durationMs: 2500,
      });
      return;
    }
    setIsSendingStk(true);
    await new Promise(resolve => setTimeout(resolve, 600));
    setIsSendingStk(false);
    openSuccessModal('Mpesa', toBePaid, 'STK');
  };

  const handleBackToShop = async () => {
    setPaymentModalVisible(false);
    const ctx = buildAppAnalyticsContext();
    appAnalytics.track('demo_finished', { ...ctx });
    try {
      await clearDemoData();
      dispatch(resetCheckoutPaymentIntent());
    } catch (error) {
      console.error('[DemoCheckoutScreen] Failed to clear demo data:', error);
    }
    const parentNavigation = navigation.getParent?.();
    if (parentNavigation?.replace) {
      parentNavigation.replace('OnboardingCarousel');
      return;
    }
    if (parentNavigation?.navigate) {
      parentNavigation.navigate('OnboardingCarousel');
      return;
    }
    navigation.replace('DemoIntro');
  };

  const networkAvailableGlobal = true;
  const mpesaconfigured = true;
  const mpesaStkCtaLabel = 'Send STK Push';
  const shouldRunDemoPulse = !paymentModalVisible;

  return (
    <>
      <KeyboardAwareFormScroll
        style={styles.scrollViewContent}
        bottomOffset={v(10)}
        baseBottomPadding={v(10)}
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps="handled"
        extraKeyboardSpace={20}>
        <View style={{ flex: 1 }}>
          <Receipt
            screenName="CheckoutScreen"
            order={order}
            Payment_Information={Payment_Information}
            minHeightFactor={0.35}
            maxHeightFactor={0.6}
          />

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: s(8),
              marginTop: v(40),
              justifyContent: 'center',
            }}>
            {order?.length > 1 ? (
              <TouchableOpacity
                onPress={() => {
                  // setPayForSelectedItemsOnly(!payForSelectedItemsOnly)
                  AppSnackbar.show(
                    'Pay For Specific Products only is not supported in demo mode, Please create a BizPlus POS account to use this feature.',
                    'info',
                    { durationMs: 7500 },
                  );
                }}
                style={styles.checkboxContainer}>
                {payForSelectedItemsOnly ? (
                  <Feather
                    name="check-square"
                    size={24}
                    color={theme.colors.success}
                  />
                ) : (
                  <Feather name="square" size={24} color={theme.colors.text} />
                )}
                <Typography
                  style={{
                    fontSize: fs(16),
                    fontFamily: GTFonts.regular,
                    lineHeight: fs(18),
                    color: payForSelectedItemsOnly
                      ? theme.colors.success
                      : theme.colors.text,
                  }}>
                  Pay For Specific Products only
                </Typography>
              </TouchableOpacity>
            ) : null}
          </View>

        </View>
      </KeyboardAwareFormScroll>

      <DemoPaymentModal
        visible={paymentModalVisible}
        paymentInfo={paymentInfo}
        onBackToShop={handleBackToShop}
      />

      <CustomBottomSheet
        bottomSheetModalRef={MpesaBottomSheetModalRef}
        snapPoints={['60%', '90%']}
        enableDynamicSizing={false}
        handleSheetChanges={() => {}}
        constrainWidth
        footer={
          payment.cash ? (
            <View style={styles.sheetFooter}>
              <DemoAttentionPulse
                active={
                  shouldRunDemoPulse &&
                  Boolean(input) &&
                  Number(input) >= (toBePaid ?? 0)
                }>
                <Button
                  text={`Confirm Cash Payment - ${currencyTextCapital} ${formatMoney(toBePaid ?? 0)}`}
                  height={v(44)}
                  fontSize={fs(14)}
                  borderRadius={s(8)}
                  backgroundColor={
                    input && Number(input) > 0
                      ? theme.colors.success
                      : theme.colors.borderGray
                  }
                  disabled={!input || Number(input) < (toBePaid ?? 0)}
                  onPress={() => {
                    MpesaBottomSheetModalRef.current?.dismiss();
                    handlePayByCash();
                  }}
                />
              </DemoAttentionPulse>
            </View>
          ) : payment.mpesa && mpesaActiveTab === 'LATEST' ? (
            <View style={styles.sheetFooter}>
              <DemoAttentionPulse
                active={
                  shouldRunDemoPulse &&
                  Boolean(input) &&
                  Number(input) >= (toBePaid ?? 0)
                }>
                <Button
                  text={`Confirm Mpesa Till Payment - ${currencyTextCapital} ${formatMoney(toBePaid ?? 0)}`}
                  height={v(44)}
                  fontSize={fs(14)}
                  borderRadius={s(8)}
                  backgroundColor={
                    input &&
                    Number(input) > 0 &&
                    Number(input) >= (toBePaid ?? 0)
                      ? theme.colors.success
                      : theme.colors.borderGray
                  }
                  disabled={
                    !input ||
                    Number(input) <= 0 ||
                    Number(input) < (toBePaid ?? 0)
                  }
                  onPress={() => {
                    MpesaBottomSheetModalRef.current?.dismiss();
                    handleManualMpesaPayment();
                  }}
                />
              </DemoAttentionPulse>
            </View>
          ) : payment.mpesa && mpesaActiveTab === 'STK' ? (
            <View style={styles.sheetFooter}>
              <DemoAttentionPulse
                active={
                  shouldRunDemoPulse &&
                  safaricomNumberValidation(
                    normalizeSafaricomPhone(mpesaPhone),
                  ) && !isSendingStk
                }>
                <Button
                  text={
                    isSendingStk
                      ? `Sending... - ${currencyTextCapital} ${formatMoney(toBePaid ?? 0)}`
                      : `${mpesaStkCtaLabel} - ${currencyTextCapital} ${formatMoney(toBePaid ?? 0)}`
                  }
                  height={v(44)}
                  fontSize={fs(14)}
                  borderRadius={s(8)}
                  backgroundColor={
                    safaricomNumberValidation(
                      normalizeSafaricomPhone(mpesaPhone),
                    )
                      ? theme.colors.success
                      : theme.colors.graphite
                  }
                  disabled={
                    !safaricomNumberValidation(
                      normalizeSafaricomPhone(mpesaPhone),
                    ) || isSendingStk
                  }
                  onPress={requestMpesaStkPush}
                />
              </DemoAttentionPulse>
            </View>
          ) : undefined
        }>
        <View style={styles.sheetContent}>
          <Typography style={styles.sheetTitle}>
            Select Payment Method
          </Typography>

          <View style={styles.paymentOptionRow}>
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => handleSetPaymentValues(true, false)}
              style={[
                styles.paymentOptionCard,
                payment.cash && styles.paymentOptionCardActive,
              ]}>
              <Image
                source={require('../../assets/cash.png')}
                style={styles.paymentOptionIcon}
              />
              <Typography style={styles.paymentOptionLabel}>Cash</Typography>
            </TouchableOpacity>
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => {
                handleSetPaymentValues(false, true);
                setMpesaActiveTab('LATEST');
                dispatch(
                  setCheckoutPaymentIntent({
                    method: 'mpesa',
                    mpesa: { mode: 'PAID_TO_TILL' },
                  }),
                );
                dispatch(
                  setCheckoutPaymentSelection({
                    cash: false,
                    mpesa: true,
                    payLater: false,
                  }),
                );
              }}
              style={[
                styles.paymentOptionCard,
                payment.mpesa && styles.paymentOptionCardActive,
              ]}>
              <Image
                source={require('../../assets/mpesa.png')}
                style={styles.paymentOptionIcon}
              />
              <Typography style={styles.paymentOptionLabel}>Mpesa</Typography>
            </TouchableOpacity>
          </View>

          {payment.cash ? (
            <>
              <Typography style={styles.sheetSectionTitle}>
                Amount received
              </Typography>
              <BottomSheetTextInput
                style={styles.sheetTextInput}
                placeholder="Enter amount"
                placeholderTextColor={theme.colors.textSecondary}
                keyboardType="decimal-pad"
                value={formatNumericStringWithCommas(input)}
                onChangeText={raw => {
                  const sanitized = sanitizeMoneyInput(raw);
                  setInput(sanitized);
                  dispatch(
                    setCheckoutPaymentIntent({
                      method: 'cash',
                      cashAmount: sanitized,
                    }),
                  );
                  dispatch(
                    setCheckoutPaymentSelection({
                      cash: true,
                      mpesa: false,
                      payLater: false,
                    }),
                  );
                }}
              />
            </>
          ) : (
            <>
              <View style={styles.sheetHeader}>
                <Pressable
                  onPress={() => {
                    setMpesaActiveTab('LATEST');
                    dispatch(
                      setCheckoutPaymentIntent({
                        method: 'mpesa',
                        mpesa: { mode: 'PAID_TO_TILL' },
                      }),
                    );
                    dispatch(
                      setCheckoutPaymentSelection({
                        cash: false,
                        mpesa: true,
                        payLater: false,
                      }),
                    );
                  }}
                  style={[
                    styles.tabItem,
                    mpesaActiveTab === 'LATEST' && styles.tabItemActive,
                  ]}>
                  <Typography
                    style={[
                      styles.tabText,
                      mpesaActiveTab === 'LATEST' && styles.tabTextActive,
                    ]}>
                    Paid to Till
                  </Typography>
                </Pressable>
                <Pressable
                  onPress={() => {
                    setMpesaActiveTab('STK');
                    dispatch(
                      setCheckoutPaymentIntent({
                        method: 'mpesa',
                        mpesa: { mode: 'STK' },
                      }),
                    );
                    dispatch(
                      setCheckoutPaymentSelection({
                        cash: false,
                        mpesa: true,
                        payLater: false,
                      }),
                    );
                  }}
                  style={[
                    styles.tabItem,
                    mpesaActiveTab === 'STK' && styles.tabItemActive,
                  ]}>
                  <Typography
                    style={[
                      styles.tabText,
                      mpesaActiveTab === 'STK' && styles.tabTextActive,
                    ]}>
                    Send STK Prompt
                  </Typography>
                </Pressable>
              </View>

              {mpesaActiveTab === 'STK' ? (
                <View style={styles.sheetMpesaContent}>
                  <Typography style={styles.sheetLabel}>
                    Customer Mpesa number
                  </Typography>
                  <View
                    style={{
                      backgroundColor: theme.colors.surface,
                      height: v(50),
                      marginBottom: v(6),
                      borderRadius: s(10),
                      borderWidth: StyleSheet.hairlineWidth,
                      borderColor: theme.colors.borderGray,
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}>
                    <PhoneInput
                      ref={phoneInputRef}
                      defaultCountry={countryCode as any}
                      value={phoneInputValue}
                      modalType="bottomSheet"
                      placeholder="720 222 222"
                      onPress={() => MpesaBottomSheetModalRef.current?.expand()}
                      minBottomsheetHeight={'50%'}
                      initialBottomsheetHeight={'80%'}
                      maxBottomsheetHeight={'92%'}
                      popularCountries={PREFERRED_COUNTRIES}
                      modalPopularCountriesTitle="Popular countries"
                      modalAllCountriesTitle="All countries"
                      onChangePhoneNumber={handleMpesaPhoneChange}
                      country={phoneSelectedCountry}
                      onChangeCountry={handleMpesaCountryChange}
                      phoneInputStyles={{
                        container: {
                          backgroundColor: 'transparent',
                          height: '100%',
                          alignItems: 'center',
                        },
                        flagContainer: {
                          paddingHorizontal: s(8),
                        },
                        divider: {
                          width: s(Platform.OS === 'web' ? 0.5 : 1),
                        },
                        input: {
                          fontFamily: GTFonts.semibold,
                          fontSize: fs(16),
                          lineHeight: fs(18),
                          ...(Platform.OS === 'web' && {
                            outlineStyle: 'none',
                          }),
                        } as any,
                        callingCode: {
                          fontFamily: GTFonts.semibold,
                          fontSize: fs(16),
                          lineHeight: fs(18),
                        },
                      }}
                    />
                  </View>
                  {!safaricomNumberValidation(
                    normalizeSafaricomPhone(mpesaPhone),
                  ) && mpesaPhone.length > 0 ? (
                    <Typography style={styles.validationText}>
                      Enter a valid Safaricom number
                    </Typography>
                  ) : null}
                </View>
              ) : (
                <View style={styles.sheetMpesaContent}>
                  <Typography style={styles.sheetSectionTitle}>
                    Amount received
                  </Typography>
                  <BottomSheetTextInput
                    style={styles.sheetTextInput}
                    placeholder="Enter amount"
                    placeholderTextColor={theme.colors.textSecondary}
                    keyboardType="decimal-pad"
                    value={formatNumericStringWithCommas(input)}
                    onChangeText={raw => {
                      const sanitized = sanitizeMoneyInput(raw);
                      setInput(sanitized);
                      dispatch(
                        setCheckoutPaymentIntent({
                          method: 'mpesa',
                          cashAmount: sanitized,
                          mpesa: { mode: 'PAID_TO_TILL' },
                        }),
                      );
                      dispatch(
                        setCheckoutPaymentSelection({
                          cash: false,
                          mpesa: true,
                          payLater: false,
                        }),
                      );
                    }}
                  />
                </View>
              )}
            </>
          )}
        </View>
      </CustomBottomSheet>

      <KeyboardStickyView offset={{ closed: 0, opened: 30 }}>
        <View style={styles.stickyButtonContainer}>
          {payForSelectedItemsOnly ? (
            <Button
              text="Continue"
              fontSize={fs(14)}
              rippleColor={theme.colors.textGray600}
              borderRadius={s(8)}
              height={v(46)}
              disabled={!networkAvailableGlobal}
              backgroundColor={theme.colors.success}
              onPress={() => {
                AppSnackbar.show(
                  'Partial-order is not supported in demo mode, Please create a BizPlus POS account to use this feature.',
                  'info',
                  { durationMs: 7500 },
                );
              }}
            />
          ) : payment.cash && input && Number(input) >= (toBePaid ?? 0) ? (
            <DemoAttentionPulse
              active={
                shouldRunDemoPulse && networkAvailableGlobal && !isloading
              }>
              <View style={styles.splitButtonContainer}>
                <TouchableOpacity
                  style={[
                    styles.splitButtonMain,
                    !networkAvailableGlobal && styles.splitButtonDisabled,
                  ]}
                  disabled={!networkAvailableGlobal || isloading}
                  activeOpacity={0.8}
                  onPress={() => {
                    handlePayByCash();
                  }}>
                  <Typography style={styles.splitButtonText}>
                    {isloading
                      ? 'Processing...'
                      : `Confirm Cash Payment - ${currencyTextCapital} ${formatMoney(Number(input))}`}
                  </Typography>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.splitButtonEdit,
                    !networkAvailableGlobal && styles.splitButtonDisabled,
                  ]}
                  disabled={!networkAvailableGlobal}
                  activeOpacity={0.8}
                  onPress={() => MpesaBottomSheetModalRef.current?.present()}>
                  <Feather
                    name="edit-2"
                    size={iconSize(18)}
                    color={theme.colors.surface}
                  />
                </TouchableOpacity>
              </View>
            </DemoAttentionPulse>
          ) : payment.mpesa &&
            mpesaActiveTab === 'LATEST' &&
            input &&
            Number(input) >= (toBePaid ?? 0) ? (
            <DemoAttentionPulse
              active={
                shouldRunDemoPulse && networkAvailableGlobal && !isloading
              }>
              <View style={styles.splitButtonContainer}>
                <TouchableOpacity
                  style={[
                    styles.splitButtonMain,
                    (!networkAvailableGlobal || isloading) &&
                      styles.splitButtonDisabled,
                  ]}
                  disabled={!networkAvailableGlobal || isloading}
                  activeOpacity={0.8}
                  onPress={() => {
                    handleManualMpesaPayment();
                  }}>
                  <Typography style={styles.splitButtonText}>
                    {isloading
                      ? 'Processing...'
                      : `Confirm Mpesa Till Payment - ${currencyTextCapital} ${formatMoney(Number(input))}`}
                  </Typography>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.splitButtonEdit,
                    !networkAvailableGlobal && styles.splitButtonDisabled,
                  ]}
                  disabled={!networkAvailableGlobal}
                  activeOpacity={0.8}
                  onPress={() => MpesaBottomSheetModalRef.current?.present()}>
                  <Feather
                    name="edit-2"
                    size={iconSize(18)}
                    color={theme.colors.surface}
                  />
                </TouchableOpacity>
              </View>
            </DemoAttentionPulse>
          ) : payment.mpesa &&
            safaricomNumberValidation(normalizeSafaricomPhone(mpesaPhone)) ? (
            <DemoAttentionPulse
              active={
                shouldRunDemoPulse && networkAvailableGlobal && !isSendingStk
              }>
              <View style={styles.splitButtonContainer}>
                <TouchableOpacity
                  style={[
                    styles.splitButtonMain,
                    !networkAvailableGlobal && styles.splitButtonDisabled,
                  ]}
                  disabled={!networkAvailableGlobal || isSendingStk}
                  activeOpacity={0.8}
                  onPress={() => {
                    requestMpesaStkPush();
                  }}>
                  <Typography style={styles.splitButtonText}>
                    {isSendingStk
                      ? 'Sending...'
                      : `${mpesaStkCtaLabel} - ${currencyTextCapital} ${formatMoney(toBePaid ?? 0)}`}
                  </Typography>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.splitButtonEdit,
                    !networkAvailableGlobal && styles.splitButtonDisabled,
                  ]}
                  disabled={!networkAvailableGlobal}
                  activeOpacity={0.8}
                  onPress={() => MpesaBottomSheetModalRef.current?.present()}>
                  <Feather
                    name="edit-2"
                    size={iconSize(18)}
                    color={theme.colors.surface}
                  />
                </TouchableOpacity>
              </View>
            </DemoAttentionPulse>
          ) : (
            <Button
              text="Pay Now"
              fontSize={fs(14)}
              rippleColor={theme.colors.textGray600}
              borderRadius={s(8)}
              height={v(46)}
              disabled={!networkAvailableGlobal}
              backgroundColor={theme.colors.success}
              onPress={() => MpesaBottomSheetModalRef.current?.present()}
            />
          )}
        </View>
      </KeyboardStickyView>
    </>
  );
};

export default DemoCheckoutScreen;

function createDemoCheckoutStyles(constrainWidth: boolean) {
  return StyleSheet.create({
    scrollViewContent: {
      flex: 1,
      backgroundColor: theme.colors.background,
      ...(constrainWidth && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        paddingHorizontal: s(24),
      }),
    },
    scrollContainer: {
      flexGrow: 1,
    },
    checkboxContainer: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: s(8),
    },
    stickyButtonContainer: {
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(8),
      paddingVertical: s(20),
      borderTopWidth: 1,
      borderTopColor: theme.colors.borderGray200,
      ...(constrainWidth && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        paddingHorizontal: s(24),
      }),
    },
    splitButtonContainer: {
      flexDirection: 'row',
      height: v(46),
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
    },
    splitButtonMain: {
      flex: 0.8,
      backgroundColor: theme.colors.success,
      justifyContent: 'center',
      alignItems: 'center',
      borderRightWidth: 1,
      borderRightColor: theme.colors.surface,
    },
    splitButtonEdit: {
      flex: 0.2,
      backgroundColor: theme.colors.success,
      justifyContent: 'center',
      alignItems: 'center',
    },
    splitButtonText: {
      color: theme.colors.surface,
      fontSize: fs(14),
      lineHeight: fs(18),
      fontFamily: GTFonts.semibold,
    },
    splitButtonDisabled: {
      opacity: 0.6,
    },
    sheetTitle: {
      fontFamily: GTFonts.regular,
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: v(12),
    },
    paymentOptionRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: v(16),
      gap: s(8),
    },
    paymentOptionCard: {
      flex: 1,
      borderWidth: 1,
      borderColor: theme.colors.borderGray200,
      borderRadius: s(8),
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(10),
      backgroundColor: theme.colors.surface,
    },
    paymentOptionCardActive: {
      borderColor: theme.colors.success,
      backgroundColor: theme.colors.successLight,
    },
    paymentOptionIcon: {
      width: s(70),
      height: v(28),
      marginBottom: v(6),
      resizeMode: 'contain',
    },
    paymentOptionLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    sheetHeader: {
      flexDirection: 'row',
      gap: s(8),
      paddingHorizontal: s(8),
      paddingVertical: s(8),
    },
    tabItem: {
      flex: 1,
      borderWidth: 1,
      borderColor: theme.colors.borderGray200,
      borderRadius: s(8),
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(10),
      backgroundColor: theme.colors.surface,
    },
    tabItemActive: {
      backgroundColor: theme.colors.successLight,
      borderColor: theme.colors.successDark,
    },
    tabText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      color: theme.colors.text,
    },
    tabTextActive: {
      color: theme.colors.success,
      fontWeight: 'bold',
    },
    sheetContent: {
      paddingHorizontal: s(12),
      paddingVertical: s(8),
    },
    sheetSectionTitle: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
      marginTop: v(24),
    },
    sheetLabel: {
      fontFamily: GTFonts.regular,
      lineHeight: fs(18),
      fontSize: fs(14),
      color: theme.colors.text,
      marginBottom: v(6),
      marginTop: v(10),
    },
    sheetTextInput: {
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      height: v(50),
      borderColor: theme.colors.borderGray,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(12),
      paddingVertical: s(10),
      fontFamily: GTFonts.regular,
      fontSize: fs(16),
      color: theme.colors.text,
      backgroundColor: theme.colors.surface,
    },
    validationText: {
      marginTop: v(6),
      color: theme.colors.error,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
    },
    sheetFooter: {
      paddingHorizontal: s(12),
      backgroundColor: theme.colors.surface,
    },
    sheetMpesaContent: {
      paddingHorizontal: s(12),
      paddingVertical: s(8),
    },
  });
}
